// App-wide call surface.
//
// Mounted once inside the authenticated layout so an incoming or ongoing call
// rings and can be answered from any page — the user no longer has to sit in
// the project chat to see it. All lifecycle decisions stay server-owned; the
// participant auth token lives in component state only.
import { lazy, Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, PhoneIncoming, PhoneOff } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { useCallsEnabled } from "@/hooks/use-project-calls";
import { useMyCall } from "@/hooks/use-my-call";
import { acceptCall, callErrorKey, cancelCall, declineCall, endCall, joinCall } from "@/lib/calls";
import { isLive } from "@/lib/calls.core";
import { CallOrb, CallPanel, RingCountdown } from "@/components/calls/CallVisuals";
import { useCallTone } from "@/hooks/use-call-tones";

const CallStage = lazy(() => import("@/components/calls/CallStage"));

export function GlobalCallCenter() {
  const { t } = useTranslation();
  const enabled = useCallsEnabled();
  const { call, role, ringExpired, setCall } = useMyCall();
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [joining, setJoining] = useState(false);
  const joinedForRef = useRef<string | null>(null);

  const fail = useCallback((code: string | undefined) => toast.error(t(callErrorKey(code))), [t]);

  const liveCall = call && isLive(call.status) ? call : null;

  useEffect(() => {
    if (!liveCall) {
      setAuthToken(null);
      joinedForRef.current = null;
    }
  }, [liveCall]);

  const join = useCallback(async () => {
    if (!liveCall || joining) return;
    if (joinedForRef.current === liveCall.id) return;
    setJoining(true);
    const res = await joinCall(liveCall.id);
    setJoining(false);
    if (!res.ok || !res.data?.auth_token) {
      fail(res.error_code);
      return;
    }
    joinedForRef.current = liveCall.id;
    setAuthToken(res.data.auth_token);
  }, [liveCall, joining, fail]);

  // Both sides may join as soon as the call is accepted.
  useEffect(() => {
    if (!liveCall || !role) return;
    if (["accepted", "connecting", "active"].includes(liveCall.status)) void join();
  }, [liveCall, role, join]);

  const accept = async () => {
    if (!liveCall) return;
    const res = await acceptCall(liveCall.id);
    if (!res.ok) {
      fail(res.error_code);
      return;
    }
    if (res.data?.call) setCall(res.data.call as unknown as Record<string, unknown>);
  };

  const decline = async () => {
    if (!liveCall) return;
    const res = await declineCall(liveCall.id);
    if (!res.ok) fail(res.error_code);
  };

  const cancel = async () => {
    if (!liveCall) return;
    const res = await cancelCall(liveCall.id);
    if (!res.ok) fail(res.error_code);
  };

  const finish = useCallback(async () => {
    if (!liveCall) return;
    setAuthToken(null);
    const res = await endCall(liveCall.id);
    if (!res.ok) fail(res.error_code);
  }, [liveCall, fail]);

  const showIncoming = useMemo(
    () => !!liveCall && role === "callee" && liveCall.status === "ringing" && !ringExpired,
    [liveCall, role, ringExpired],
  );
  const showOutgoing = useMemo(
    () =>
      !!liveCall &&
      role === "caller" &&
      ["provisioning", "ringing"].includes(liveCall.status) &&
      !ringExpired,
    [liveCall, role, ringExpired],
  );
  const showStage = !!liveCall && !!authToken;

  useCallTone(showIncoming ? "ringtone" : showOutgoing ? "ringback" : null);

  if (enabled !== true) return null;

  return (
    <>
      {/* Outgoing: ringing */}
      <Dialog open={showOutgoing} onOpenChange={(open) => !open && void cancel()}>
        <DialogContent className="border-none bg-transparent p-0 shadow-none sm:max-w-sm">
          <CallPanel>
            <DialogTitle className="sr-only">{t("calls.calling")}</DialogTitle>
            <div className="flex flex-col items-center gap-5 text-center">
              <RingCountdown expiresAt={liveCall?.ring_expires_at}>
                <CallOrb mode={liveCall?.mode ?? "audio"} pulsing />
              </RingCountdown>
              <div className="space-y-1">
                <p className="text-lg font-semibold tracking-tight">
                  {liveCall?.status === "provisioning" ? t("calls.connecting") : t("calls.ringing")}
                </p>
                <DialogDescription className="text-xs text-muted-foreground">
                  {t("calls.noRecording")}
                </DialogDescription>
              </div>
              <Button
                type="button"
                variant="destructive"
                className="w-full rounded-full transition-transform active:scale-[0.98]"
                onClick={() => void cancel()}
              >
                <PhoneOff className="mr-2 h-4 w-4" />
                {t("calls.cancel")}
              </Button>
            </div>
          </CallPanel>
        </DialogContent>
      </Dialog>

      {/* Incoming: accept or decline — visible on every page */}
      <Dialog open={showIncoming} onOpenChange={(open) => !open && void decline()}>
        <DialogContent className="border-none bg-transparent p-0 shadow-none sm:max-w-sm">
          <CallPanel>
            <DialogTitle className="sr-only">{t("calls.incoming")}</DialogTitle>
            <div className="flex flex-col items-center gap-5 text-center">
              <RingCountdown expiresAt={liveCall?.ring_expires_at}>
                <CallOrb mode={liveCall?.mode ?? "audio"} pulsing />
              </RingCountdown>
              <div className="space-y-1">
                <p className="flex items-center justify-center gap-2 text-lg font-semibold tracking-tight">
                  <PhoneIncoming className="h-4 w-4 text-primary" />
                  {liveCall?.mode === "video"
                    ? t("calls.incomingVideo")
                    : t("calls.incomingAudio")}
                </p>
                <DialogDescription className="text-xs text-muted-foreground">
                  {t("calls.noRecording")}
                </DialogDescription>
              </div>
              <div className="grid w-full grid-cols-2 gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-full transition-transform active:scale-[0.98]"
                  onClick={() => void decline()}
                >
                  {t("calls.decline")}
                </Button>
                <Button
                  type="button"
                  className="rounded-full transition-transform active:scale-[0.98]"
                  onClick={() => void accept()}
                >
                  {t("calls.accept")}
                </Button>
              </div>
            </div>
          </CallPanel>
        </DialogContent>
      </Dialog>

      {/* Live media */}
      <Dialog open={showStage} onOpenChange={(open) => !open && void finish()}>
        <DialogContent className="max-w-4xl border-none bg-transparent p-0 shadow-none">
          <DialogTitle className="sr-only">{t("calls.inCall")}</DialogTitle>
          <DialogDescription className="sr-only">{t("calls.noRecording")}</DialogDescription>
          <CallPanel className="p-4 sm:p-5">
            <div className="min-h-[340px]">
              {showStage && authToken && liveCall ? (
                <Suspense
                  fallback={
                    <div className="flex h-[340px] items-center justify-center">
                      <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                    </div>
                  }
                >
                  <CallStage
                    authToken={authToken}
                    mode={liveCall.mode}
                    activeAt={liveCall.active_at}
                    onJoined={() => undefined}
                    onLeft={() => void finish()}
                    onEnd={() => void finish()}
                  />
                </Suspense>
              ) : null}
            </div>
          </CallPanel>
        </DialogContent>
      </Dialog>
    </>
  );
}
